import React, { useEffect, useMemo, useState } from 'react';
import MoveToCategoryModal from '../../../../components/MoveToCategoryModal/MoveToCategoryModal';
import { useCategoriesContext } from '../../../../context/CategoriesContext';
import { IUserMenuCategory } from '../../categoriesManagement.types';
import { LANG_TEXT } from '../../../../constants/language';
import { useTranslation } from 'react-i18next';

interface Props {
  open: boolean;
  handleClose: () => void;
  categories: IUserMenuCategory[];
  selectedFoodIds: string[];
  setToast: React.Dispatch<React.SetStateAction<{ type: 'error' | 'success'; content: string }>>;
  setOpenToast: React.Dispatch<React.SetStateAction<boolean>>;
  onOpenManageModal: () => void;
}

const MoveToCategoryManager: React.FC<Props> = ({ open, handleClose, categories, selectedFoodIds, setToast, setOpenToast, onOpenManageModal }) => {
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [partialCategoryIds, setPartialCategoryIds] = useState<string[]>([]);
  const [initialCommonCategoryIds, setInitialCommonCategoryIds] = useState<string[]>([]);
  const [manuallyRemovedCategoryIds, setManuallyRemovedCategoryIds] = useState<string[]>([]);
  const { clearSelection, moveFoodsToCategories, foodMenu } = useCategoriesContext();
  const { t } = useTranslation();
  useEffect(() => {
    if (open && selectedFoodIds.length > 0 && foodMenu.foods.length > 0) {
      const selectedFoods = foodMenu.foods.filter((f) => selectedFoodIds.includes(f.id));
      if (selectedFoods.length === 0) {
        setSelectedCategoryIds([]);
        setPartialCategoryIds([]);
        return;
      }

      const existingCategoryIds = new Set(categories.map((c) => c.id));

      const candidateIds = new Set<string>();
      selectedFoods.forEach((food) => {
        const cats = food.categories_menu_ids || [];
        cats.forEach((cat: any) => {
          const id = typeof cat === 'string' ? cat : cat.id;
          if (existingCategoryIds.has(id)) candidateIds.add(id);
        });
      });

      const common: string[] = [];
      const partial: string[] = [];

      candidateIds.forEach((catId) => {
        const count = selectedFoods.reduce((acc, food) => {
          const foodCats = food.categories_menu_ids || [];
          const has = foodCats.some((cat: any) => (typeof cat === 'string' ? cat : cat.id) === catId);
          return acc + (has ? 1 : 0);
        }, 0);

        if (count === selectedFoods.length) common.push(catId);
        else if (count > 0) partial.push(catId);
      });

      setSelectedCategoryIds(common);
      setPartialCategoryIds(partial);
      setInitialCommonCategoryIds(common);
      setManuallyRemovedCategoryIds([]);
    } else if (!open) {
      setSelectedCategoryIds([]);
      setPartialCategoryIds([]);
      setInitialCommonCategoryIds([]);
      setManuallyRemovedCategoryIds([]);
    }
  }, [open, selectedFoodIds, foodMenu.foods, categories]);

  const handleAddNewCategory = () => {
    handleClose();
    onOpenManageModal();
  };

  const handleSelectCategory = (ids: string[], toggledCategoryId?: string) => {
    const wasSelected = toggledCategoryId ? selectedCategoryIds.includes(toggledCategoryId) : false;
    const isNowSelected = toggledCategoryId ? ids.includes(toggledCategoryId) : false;

    setSelectedCategoryIds(ids);
    if (toggledCategoryId) {
      setPartialCategoryIds(prev => prev.filter(pId => pId !== toggledCategoryId));
      if ((wasSelected || partialCategoryIds.includes(toggledCategoryId)) && !isNowSelected) {
        setManuallyRemovedCategoryIds(prev => Array.from(new Set([...prev, toggledCategoryId!])));
      } else if (!wasSelected && isNowSelected) {
        setManuallyRemovedCategoryIds(prev => prev.filter(id => id !== toggledCategoryId));
      }
    }
  };

  const onConfirm = async () => {
    if (selectedCategoryIds.length === 0 && manuallyRemovedCategoryIds.length === 0) return;

    try {
      await moveFoodsToCategories(selectedFoodIds, selectedCategoryIds, initialCommonCategoryIds, manuallyRemovedCategoryIds);

      setToast({ content: t(LANG_TEXT.UPDATE_SUCCESS_MESSAGE), type: 'success' });
      setOpenToast(true);
      setSelectedCategoryIds([]);
      setManuallyRemovedCategoryIds([]);
      clearSelection();
      handleClose();
    } catch (ex) {
      setToast({ content: t(LANG_TEXT.UPDATE_FAILED_MESSAGE), type: 'error' });
      setOpenToast(true);
    }
  };

  return (
    <MoveToCategoryModal
      open={open}
      handleClose={handleClose}
      categories={categories}
      selectedCategoryIds={selectedCategoryIds}
      partialCategoryIds={partialCategoryIds}
      onSelectCategory={handleSelectCategory}
      onConfirm={onConfirm}
      onAddNewCategory={handleAddNewCategory}
    />
  );
};

export default MoveToCategoryManager;
